import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { GameOptions } from "../../../core/protocol/types";
import "./MultiPlayer.css";

const DEFAULT_MAX_PLAYERS = 6;
const ORCHESTRATOR_URL = process.env.NODE_ENV === 'production' 
    ? window.location.origin 
    : "http://localhost:3001";

const HostEntryPoint: React.FC = () => {
  const navigate = useNavigate();
  const [savedPlayerName, setSavedPlayerName] = useLocalStorage("playerName", "");

  const [playerName, setPlayerName] = useState<string>(
    (savedPlayerName as string) || ""
  );
  const [serverName, setServerName] = useState<string>(
    (savedPlayerName as string) ? `${savedPlayerName}'s Game` : ""
  );
  const [gameMode, setGameMode] =
    useState<GameOptions["gameMode"]>("standard");
  const [maxPlayers, setMaxPlayers] = useState<number>(DEFAULT_MAX_PLAYERS);
  const [error, setError] = useState<string>("");
  const [isBusy, setIsBusy] = useState(false);
  const [statusLabel, setStatusLabel] = useState("");

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setIsBusy(true);
    setStatusLabel("Requesting server...");

    const trimmedName = playerName.trim() || "QuadraHost";
    setSavedPlayerName(trimmedName);

    if (!serverName.trim()) {
        setError("Server name is required");
        setIsBusy(false);
        setStatusLabel("");
        return;
    }

    const options = {
      gameMode,
      maxPlayers,
      serverName: serverName.trim()
    };

    try {
      // Call the Local Orchestrator to spawn a server
      const response = await fetch(`${ORCHESTRATOR_URL}/create-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(options),
      });

      if (!response.ok) {
        throw new Error(`Failed to create session: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("Session created:", data);

      setStatusLabel("Server ready! Joining...");
      
      setTimeout(() => {
         // Navigate to the join route with the wsUrl
         const joinUrl = `/multi-player/join/manual?url=${encodeURIComponent(data.wsUrl)}`;
         navigate(joinUrl);
      }, 500);

    } catch (err: any) {
      console.error("Host error:", err);
      setError(err?.message || "Failed to start server");
      setIsBusy(false);
      setStatusLabel("");
    }
  };

  return (
    <div className="multiplayer-setup-wrapper">
      <button className="back-button" onClick={() => navigate("/")}>
        <span>← Back</span>
      </button>
      <div className="multiplayer-setup-content">
        <form className="multiplayer-setup-container" onSubmit={handleSubmit}>
          <h2>Host Multiplayer Game</h2>

          <div className="input-group">
            <label htmlFor="playerName">Player Name</label>
            <input
              id="playerName"
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              disabled={isBusy}
              placeholder="Enter your name"
            />
          </div>

          <div className="input-group">
            <button
                type="button"
                className="secondary-button"
                style={{ width: '100%', marginBottom: '1rem' }}
                onClick={() => navigate("/multi-player/public")}
            >
                Browse Public Games
            </button>
          </div>

          <div className="input-group">
            <label htmlFor="serverName">Server Name</label>
            <input
              id="serverName"
              type="text"
              value={serverName}
              onChange={(e) => setServerName(e.target.value)}
              disabled={isBusy}
              required
              placeholder="e.g. My Awesome Game"
            />
          </div>

          <div className="mode-selection-group">
            <label className="mode-group-label">Game Mode</label>
            <div className="mode-selection">
              {["standard", "survivor"].map((mode) => (
                <label
                  key={mode}
                  className={`mode-option ${
                    gameMode === mode ? "active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="gameMode"
                    value={mode}
                    checked={gameMode === mode}
                    onChange={() =>
                      setGameMode(mode as GameOptions["gameMode"])
                    }
                    disabled={isBusy}
                  />
                  <div className="mode-option-content">
                    <span className="mode-title">
                      {mode === "standard" ? "Standard" : "Survivor"}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="mode-selection-group">
            <label className="mode-group-label">Max Players</label>
            <div className="mode-selection horizontal" style={{ gap: "8px" }}>
              {[2, 4, 6, 8, 16].map((num) => (
                <label
                  key={num}
                  className={`mode-option ${
                    maxPlayers === num ? "active" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="maxPlayers"
                    value={num}
                    checked={maxPlayers === num}
                    onChange={() => setMaxPlayers(num)}
                    disabled={isBusy}
                  />
                  <div className="mode-option-content">
                    <span className="mode-title">{num}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {statusLabel && (
            <div className="status-banner">
              <span>{statusLabel}</span>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="action-buttons">
            <button
              type="submit"
              className="primary-button"
              disabled={isBusy}
            >
              {isBusy ? "Creating Server..." : "Start Hosting"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HostEntryPoint;
